import { getRandomColor } from '@/theme/randomColor'
import {
  BusinessLogicParameter,
  BusinessLogicRule,
  LibraryLogicType,
  NormalizedBusinessLogicParameter,
  NormalizedBusinessLogicRule,
  NormalizedStreamBusinessLogic,
  SubLabelOperator
} from '@/types/businessLogic'
import { CameraStreamWithDeviceId } from '@/types/camera'
import {
  CameraSettingsPayload,
  Deployment,
  InputSourceType
} from '@/types/deployment'
import { generateUUID } from '@/utils/uuid'

const buildNormalizedBusinessLogicRule = (
  rule: BusinessLogicRule
): NormalizedBusinessLogicRule => {
  return {
    operator: rule.operator,
    label_groups: rule.label_groups.map((labelGroup) => {
      const subLabel = (labelGroup.sub_labels || [])[0] || {
        operator: SubLabelOperator.With,
        label_ids: []
      }

      return {
        uuid: generateUUID(),
        type: labelGroup.type,
        label_id: labelGroup.label_id,
        threshold: labelGroup.threshold,
        sub_label_ids: subLabel.label_ids,
        sub_label_operator: subLabel.operator,
        min_dwell_time: labelGroup.min_dwell_time ?? null,
        alert_on_exit: labelGroup.alert_on_exit,
        alert_on_unique: labelGroup.alert_on_unique,
        is_ocr_enabled: labelGroup.ocr_label_ids.length > 0,
        ocr_label_ids: labelGroup.ocr_label_ids,
        is_qr_barcode_enabled: labelGroup.machine_code_label_ids.length > 0,
        qr_barcode_label_ids: labelGroup.machine_code_label_ids,
        classification_label_id:
          labelGroup.classification_tags?.[labelGroup.label_id] || null
      }
    })
  }
}

const buildNormalizedBusinessLogicParameter = (
  parameter: BusinessLogicParameter
): NormalizedBusinessLogicParameter => {
  return {
    type: parameter.parameter_type,
    name: parameter.name,
    color: getRandomColor(),
    coordinates: stringCoordinatesToArray(parameter.coordinates),
    notification: parameter.event.alert,
    upload_video: parameter.event.upload_video,
    upload_bbox_video: parameter.event.upload_bbox_video,
    rules: parameter.rules.map((rule) => {
      return buildNormalizedBusinessLogicRule(rule)
    })
  }
}

export const stringCoordinatesToArray = (coordinates: string): number[][] => {
  const coords = coordinates.split(';')
  const result = []

  for (let i = 0; i < coords.length; i += 2) {
    result.push([Number(coords[i]), Number(coords[i + 1])])
  }

  return result
}

export const buildSavedStreamLogic = (
  deployments: Deployment[],
  cameraStreams: CameraStreamWithDeviceId[]
): NormalizedStreamBusinessLogic[] => {
  return deployments
    .filter(
      (d) => d.settings_payload?.input_source_type === InputSourceType.Cameras
    )
    .map((deployment) => {
      const settings_payload =
        deployment.settings_payload as CameraSettingsPayload
      const streamData = cameraStreams.find(
        (stream) => stream.camera_id === settings_payload.stream_id
      )

      return settings_payload.business_logics.map((logic) => {
        return {
          logic_id: logic.id,
          logic_type: logic.logic_type as LibraryLogicType,
          logic_name: logic.name || '',
          camera_id: settings_payload.stream_id,
          camera_name: streamData?.camera_name || '',
          camera_image: streamData?.image || '',
          device_id: settings_payload.device_id,
          live_output: {
            screen: settings_payload.eglsink,
            rtsp: settings_payload.rtspsink
          },
          parameters: logic.parameters.map((parameter) => {
            return buildNormalizedBusinessLogicParameter(parameter)
          })
        }
      })
    })
    .flat()
}
